import React, { useState, useEffect } from 'react';
import { Key, RefreshCw, Copy, Check } from 'lucide-react';
import './ToolsCommon.css';

const PasswordGenerator = () => {
  const [password, setPassword] = useState('');
  const [length, setLength] = useState(16);
  const [includeUppercase, setIncludeUppercase] = useState(true);
  const [includeLowercase, setIncludeLowercase] = useState(true);
  const [includeNumbers, setIncludeNumbers] = useState(true);
  const [includeSymbols, setIncludeSymbols] = useState(true);
  const [copied, setCopied] = useState(false);

  const generatePassword = () => {
    const uppercase = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
    const lowercase = 'abcdefghijklmnopqrstuvwxyz';
    const numbers = '0123456789';
    const symbols = '!@#$%^&*()_+-=[]{}|;:,.<>?';

    let charset = '';
    if (includeUppercase) charset += uppercase;
    if (includeLowercase) charset += lowercase;
    if (includeNumbers) charset += numbers;
    if (includeSymbols) charset += symbols;

    if (charset === '') {
      setPassword('');
      return;
    }

    let newPassword = '';
    for (let i = 0; i < length; i++) {
      newPassword += charset.charAt(Math.floor(Math.random() * charset.length));
    }

    setPassword(newPassword);
  };

  const copyToClipboard = async () => {
    await navigator.clipboard.writeText(password);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const getPasswordStrength = () => {
    if (!password) return { label: 'None', color: '#9ca3af', percentage: 0 };

    let strength = 0;
    if (password.length >= 8) strength += 20;
    if (password.length >= 12) strength += 20;
    if (password.length >= 16) strength += 20;
    if (/[a-z]/.test(password) && /[A-Z]/.test(password)) strength += 20;
    if (/\d/.test(password)) strength += 10;
    if (/[!@#$%^&*()_+\-=\[\]{}|;:,.<>?]/.test(password)) strength += 10;

    if (strength >= 80) return { label: 'Very Strong', color: '#10b981', percentage: strength };
    if (strength >= 60) return { label: 'Strong', color: '#22c55e', percentage: strength };
    if (strength >= 40) return { label: 'Medium', color: '#f59e0b', percentage: strength };
    if (strength >= 20) return { label: 'Weak', color: '#f97316', percentage: strength };
    return { label: 'Very Weak', color: '#ef4444', percentage: strength };
  };

  useEffect(() => {
    generatePassword();
  }, [length, includeUppercase, includeLowercase, includeNumbers, includeSymbols]);

  const strength = getPasswordStrength();

  return (
    <div className="tool-container">
      <div className="tool-content-wrapper">
        <div className="tool-header">
          <Key size={48} className="tool-icon" />
          <h1 className="tool-title">Password Generator</h1>
          <p className="tool-subtitle">Generate secure passwords instantly</p>
        </div>

        <div className="tool-panel">
          {password && (
            <div className="password-display">
              <div className="password-text">{password}</div>
              <div style={{ marginBottom: '16px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                  <span style={{ fontSize: '14px', color: '#6b7280' }}>Strength:</span>
                  <span style={{ fontSize: '14px', fontWeight: '600', color: strength.color }}>{strength.label}</span>
                </div>
                <div style={{ width: '100%', height: '8px', backgroundColor: '#e5e7eb', borderRadius: '4px', overflow: 'hidden' }}>
                  <div style={{ width: `${strength.percentage}%`, height: '100%', backgroundColor: strength.color, transition: 'all 0.3s' }}></div>
                </div>
              </div>
              <div className="tool-actions" style={{ justifyContent: 'center' }}>
                <button onClick={copyToClipboard} className="tool-button secondary">
                  {copied ? <Check size={20} /> : <Copy size={20} />}
                  {copied ? 'Copied!' : 'Copy Password'}
                </button>
                <button onClick={generatePassword} className="tool-button primary">
                  <RefreshCw size={20} />
                  Generate New
                </button>
              </div>
            </div>
          )}

          <div className="tool-section">
            <label className="tool-label">Password Length: {length}</label>
            <input
              type="range"
              min="4"
              max="64"
              value={length}
              onChange={(e) => setLength(Number(e.target.value))}
              className="tool-range"
            />
          </div>

          <div className="tool-section">
            <label className="tool-label">Include Characters</label>
            <div className="checkbox-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={includeUppercase}
                  onChange={(e) => setIncludeUppercase(e.target.checked)}
                />
                Uppercase Letters (A-Z)
              </label>
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={includeLowercase}
                  onChange={(e) => setIncludeLowercase(e.target.checked)}
                />
                Lowercase Letters (a-z)
              </label>
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={includeNumbers}
                  onChange={(e) => setIncludeNumbers(e.target.checked)}
                />
                Numbers (0-9)
              </label>
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={includeSymbols}
                  onChange={(e) => setIncludeSymbols(e.target.checked)}
                />
                Symbols (!@#$%^&*)
              </label>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PasswordGenerator;
